import { useState } from "react";
import { Table, Button, Modal, Form, Select, Input, Popconfirm, message, Tag, Rate } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useDocClients, useFeedback, useFeedbackCreate, useFeedbackDelete } from "../../../../api-hooks/documents";

const SENTIMENT_COLORS: Record<string, string> = { positive: "green", neutral: "blue", negative: "red" };

export default function FeedbackTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [modal, setModal] = useState(false);
  const [page, setPage] = useState(1);

  const { data: clients = [] } = useDocClients(cid);
  const { data: fbData, isLoading } = useFeedback(cid, { page, limit: 50 });
  const rows = (fbData as any)?.data ?? [];
  const total = (fbData as any)?.total ?? 0;

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useFeedbackCreate(cid, cb("Feedback recorded.", () => setModal(false)));
  const del = useFeedbackDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const cols = [
    { title: "Client", dataIndex: "client_name", key: "cl" },
    { title: "Rating", dataIndex: "rating", key: "r", render: (v: number) => <Rate disabled defaultValue={v} count={5} style={{ fontSize: 12 }} /> },
    { title: "Comment", dataIndex: "comment", key: "c", render: (v: string) => v ? <span className="text-xs text-slate-600 max-w-[200px] truncate block">{v}</span> : "—" },
    { title: "Channel", dataIndex: "channel", key: "ch", render: (v: string) => <Tag>{v}</Tag> },
    { title: "Sentiment", dataIndex: "sentiment", key: "s", render: (v: string) => v ? <Tag color={SENTIMENT_COLORS[v] ?? "default"}>{v}</Tag> : "—" },
    { title: "Public", dataIndex: "is_public", key: "ip", render: (v: number) => <Tag color={v ? "green" : "default"}>{v ? "Yes" : "No"}</Tag> },
    { title: "Date", dataIndex: "created_at", key: "d", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "", key: "act", render: (_: any, r: any) => <Popconfirm title="Delete?" onConfirm={() => del.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm> },
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <div className="text-sm text-slate-500">{total} feedback records</div>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setModal(true); form.resetFields(); form.setFieldsValue({ channel: "email", rating: 5, is_public: false }); }} style={{ background: "#4c1d95" }}>Add Feedback</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={cols} loading={isLoading} size="small" pagination={{ current: page, pageSize: 50, total, onChange: setPage }} />
      <Modal open={modal} onCancel={() => setModal(false)} footer={null} destroyOnClose title="Record Client Feedback" width={440}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => create.mutate(vals)}>
          <Form.Item name="client_id" label="Client" rules={[{ required: true }]}><Select options={(clients as any[]).map((c: any) => ({ value: c.id, label: c.name }))} showSearch optionFilterProp="label" /></Form.Item>
          <Form.Item name="rating" label="Rating" rules={[{ required: true }]}><Select options={[1,2,3,4,5].map(v => ({ value: v, label: `${v} star${v > 1 ? "s" : ""}` }))} /></Form.Item>
          <Form.Item name="comment" label="Comment"><Input.TextArea rows={2} /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="channel" label="Channel"><Select options={["email","sms","portal","in_app"].map(v => ({ value: v, label: v }))} /></Form.Item>
            <Form.Item name="sentiment" label="Sentiment"><Select allowClear options={["positive","neutral","negative"].map(v => ({ value: v, label: v }))} /></Form.Item>
          </div>
          <Form.Item name="is_public" label="Public"><Select options={[{ value: 0, label: "Private" }, { value: 1, label: "Public" }]} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending}>Save</Button>
            <Button onClick={() => setModal(false)}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
